import React, { useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import logo from '../../assets/logo.png';
import { ROUTES } from '@/urls';
import { T } from '@/theme/tokens';
import { DsIcon } from '@/components/ds/icons';
import { NAV_LINKS, roleLabel, initialsFor } from './nav-model';
import DemoBubbleComponent from './demo-bubble';
import { hasUserAccessTo } from '@/lib/permissions';
import { PermissionArea } from '@cloudcost/shared-types/users';

interface SidebarProps {
  user: any;
  firstName?: string;
  unreadDisputes: number;
  demoEnabled: boolean;
  demoBubbleVisible: boolean;
  onDemoEnter: () => void;
  onDemoLeave: () => void;
  onDemoClose: () => void;
  onLogout: () => void;
}

export default function Sidebar({
  user,
  firstName,
  unreadDisputes,
  demoEnabled,
  demoBubbleVisible,
  onDemoEnter,
  onDemoLeave,
  onDemoClose,
  onLogout,
}: SidebarProps) {
  const navigate = useNavigate();
  const logoContainerRef = useRef<HTMLDivElement>(null);

  const adminActive = ({ isActive }: { isActive: boolean }) =>
    'cc-nav-link' + (isActive ? ' active' : '');

  return (
    <aside className="cc-sidebar">
      {/* logo */}
      <div
        ref={logoContainerRef}
        onMouseEnter={demoEnabled ? onDemoEnter : undefined}
        onMouseLeave={demoEnabled ? onDemoLeave : undefined}
        style={{ position: 'relative', display: 'flex', alignItems: 'center', padding: '20px 20px 18px' }}>
        <img
          src={logo}
          alt="IncreDevo"
          onClick={() => navigate(ROUTES.HOME)}
          style={{ height: 30, width: 'auto', display: 'block', cursor: 'pointer' }}
        />
        {demoEnabled && (
          <DemoBubbleComponent
            isVisible={demoBubbleVisible}
            onClose={onDemoClose}
            anchorElement={logoContainerRef.current}
            onMouseEnter={onDemoEnter}
            onMouseLeave={onDemoLeave}
          />
        )}
      </div>

      <div className="cc-sidebar-label">WORKSPACE</div>
      <nav className="cc-nav">
        {NAV_LINKS.map((link) => {
          const showDot = link.route === ROUTES.DASHBOARD && unreadDisputes > 0;
          return (
            <NavLink
              key={link.route}
              to={link.route}
              end={link.route === ROUTES.DASHBOARD}
              className={({ isActive }) => 'cc-nav-link' + (isActive ? ' active' : '')}
              data-intro-id={link.route}
              data-testid={`nav-link-${link.route.replace(/^\//, '')}`}
              title={link.label}>
              <DsIcon name={link.icon} size={20} />
              <span style={{ flex: 1 }}>{link.label}</span>
              {showDot && (
                <span
                  aria-label={`${unreadDisputes} disputes with unread messages`}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: T.danger,
                    flex: 'none',
                  }}
                />
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* bottom: administration + user */}
      <div style={{ marginTop: 'auto', padding: '14px 12px 12px', borderTop: `1px solid ${T.divider}` }}>
        <NavLink to={ROUTES.ADMINISTRATION} className={adminActive}>
          <DsIcon name="gear" size={20} />
          <span>Administration</span>
        </NavLink>
        {hasUserAccessTo(PermissionArea.ADMIN_PANEL) && (
          <NavLink
            to={ROUTES.AI}
            className={adminActive}
            title="AI administration"
            data-testid="nav-link-ai">
            <DsIcon name="spark" size={20} />
            <span>AI</span>
          </NavLink>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 10px 4px' }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              background: T.primary,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 13,
              flex: 'none',
            }}>
            {initialsFor(firstName || user?.firstName)}
          </div>
          <div className="cc-user-meta" style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: 13,
                color: T.text,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {firstName || user?.firstName || 'User'}
            </div>
            <div style={{ fontSize: 11.5, color: T.textFaint2 }}>{roleLabel(user)}</div>
          </div>
          <button
            onClick={onLogout}
            aria-label="Log out"
            title="Log out"
            style={{
              border: 'none',
              background: 'none',
              padding: 0,
              cursor: 'pointer',
              color: T.textFaint3,
              display: 'flex',
              width: 18,
              height: 18,
            }}>
            <DsIcon name="logout" size={18} />
          </button>
        </div>
      </div>
    </aside>
  );
}
